import { createRootRoute, Outlet, useLocation } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthDialog } from '@/components/AuthDialog';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { AppLayout } from '@/components/layouts/AppLayout';
import { ThemeProvider } from '@/components/ThemeProvider';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useAutoSync } from '@/hooks/useAutoSync';
import { toastError } from '@/lib/toast-helpers';
import { useAuthStore } from '@/stores';

export const Route = createRootRoute({
  component: RootComponent,
});

function RootComponent() {
  const { t } = useTranslation('common');
  const { error } = useAuthStore();
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const location = useLocation();

  // Auto-sync when network comes back online
  useAutoSync();

  // Scroll to top when navigating to a new page
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.pathname]);

  // Show error toast when auth error occurs
  useEffect(() => {
    if (error && !authDialogOpen) {
      toastError(error || t('auth.error'));
    }
  }, [error, t, authDialogOpen]);

  return (
    <ThemeProvider>
      <TooltipProvider>
        <ErrorBoundary>
          <AppLayout onLoginClick={() => setAuthDialogOpen(true)}>
            <Outlet />
          </AppLayout>
        </ErrorBoundary>
        <Toaster />
        <AuthDialog open={authDialogOpen} onOpenChange={setAuthDialogOpen} />
      </TooltipProvider>
    </ThemeProvider>
  );
}
